import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from "@/components/ui/dialog";
import { Plus, Trash2, Edit, GripVertical, ChevronUp, ChevronDown } from "lucide-react";

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "tel", label: "Phone" },
  { value: "select", label: "Dropdown" },
  { value: "textarea", label: "Text Area" },
];

const KNOWN_BACKEND_FIELDS = [
  { value: "_custom", label: "-- Custom (stored in custom_fields) --" },
  { value: "name", label: "name (Player / Contact Name)" },
  { value: "email", label: "email" },
  { value: "phone", label: "phone" },
  { value: "player_age", label: "player_age (Age Group)" },
  { value: "parent_name", label: "parent_name" },
  { value: "soccer_experience", label: "soccer_experience" },
  { value: "organization", label: "organization" },
  { value: "num_players", label: "num_players" },
  { value: "message", label: "message" },
];

const emptyField = () => ({
  id: `field_${Date.now()}`,
  label: "",
  type: "text",
  placeholder: "",
  required: false,
  enabled: true,
  maps_to: "",
  options: [],
});

const FormFieldsEditor = ({ fields = [], onChange, title = "Form Fields" }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIdx, setEditingIdx] = useState(-1);
  const [form, setForm] = useState(emptyField());

  const openNew = () => {
    setEditingIdx(-1);
    setForm(emptyField());
    setDialogOpen(true);
  };

  const openEdit = (idx) => {
    setEditingIdx(idx);
    setForm({ ...fields[idx] });
    setDialogOpen(true);
  };

  const save = () => {
    if (!form.label.trim()) return;
    const updated = [...fields];
    if (editingIdx >= 0) {
      updated[editingIdx] = form;
    } else {
      updated.push(form);
    }
    onChange(updated);
    setDialogOpen(false);
  };

  const remove = (idx) => {
    onChange(fields.filter((_, i) => i !== idx));
  };

  const move = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= fields.length) return;
    const updated = [...fields];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    onChange(updated);
  };

  const toggle = (idx) => {
    const updated = [...fields];
    updated[idx] = { ...updated[idx], enabled: !updated[idx].enabled };
    onChange(updated);
  };

  return (
    <div>
      <Label className="text-xs text-cfc-text-muted uppercase tracking-wider font-bold">{title}</Label>
      <p className="text-xs text-cfc-text-muted mb-3">Configure which fields appear on the form. Reorder, enable/disable, or add custom fields.</p>

      <div className="space-y-2 mb-3">
        {fields.map((field, idx) => (
          <div
            key={field.id}
            className={`flex items-center gap-2 p-3 rounded border ${field.enabled ? "border-cfc-border bg-white" : "border-dashed border-gray-300 bg-gray-50"}`}
            data-testid={`form-field-row-${field.id}`}
          >
            <div className="flex flex-col gap-0.5">
              <button
                onClick={() => move(idx, -1)}
                disabled={idx === 0}
                className="text-cfc-text-muted hover:text-cfc-primary disabled:opacity-30"
              >
                <ChevronUp size={14} />
              </button>
              <button
                onClick={() => move(idx, 1)}
                disabled={idx === fields.length - 1}
                className="text-cfc-text-muted hover:text-cfc-primary disabled:opacity-30"
              >
                <ChevronDown size={14} />
              </button>
            </div>
            <div className="flex-1 min-w-0">
              <span className={`text-sm font-medium ${field.enabled ? "text-cfc-primary" : "text-cfc-text-muted line-through"}`}>
                {field.label || "(unnamed)"}
              </span>
              <span className="text-[10px] text-cfc-text-muted ml-2">
                {field.type}{field.required ? " *" : ""}{field.maps_to ? ` → ${field.maps_to}` : ""}
              </span>
            </div>
            <Switch checked={field.enabled} onCheckedChange={() => toggle(idx)} />
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(idx)}>
              <Edit size={13} />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => remove(idx)}>
              <Trash2 size={13} />
            </Button>
          </div>
        ))}
        {fields.length === 0 && (
          <p className="text-sm text-cfc-text-muted text-center py-4">No form fields configured</p>
        )}
      </div>

      <Button variant="outline" size="sm" onClick={openNew} data-testid="add-form-field-btn">
        <Plus size={14} className="mr-1" /> Add Field
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="form-field-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-cfc-primary">
              {editingIdx >= 0 ? "Edit Field" : "Add Field"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div>
              <Label>Field Label</Label>
              <Input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. Player Name"
                data-testid="field-label-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Field Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="field-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Maps To (Backend Field)</Label>
                <Select value={form.maps_to || "_custom"} onValueChange={(v) => setForm({ ...form, maps_to: v === "_custom" ? "" : v })}>
                  <SelectTrigger data-testid="field-maps-to-select">
                    <SelectValue placeholder="Custom field" />
                  </SelectTrigger>
                  <SelectContent>
                    {KNOWN_BACKEND_FIELDS.map((f) => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Placeholder Text</Label>
              <Input
                value={form.placeholder}
                onChange={(e) => setForm({ ...form, placeholder: e.target.value })}
                placeholder="e.g. Enter your name..."
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch checked={form.required} onCheckedChange={(c) => setForm({ ...form, required: c })} />
                <Label>Required</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.enabled} onCheckedChange={(c) => setForm({ ...form, enabled: c })} />
                <Label>Enabled</Label>
              </div>
            </div>

            {form.type === "select" && (
              <div>
                <Label>Dropdown Options (one per line)</Label>
                <Textarea
                  value={(form.options || []).join("\n")}
                  onChange={(e) => setForm({ ...form, options: e.target.value.split("\n") })}
                  rows={4}
                  placeholder={"Option 1\nOption 2\nOption 3"}
                  data-testid="field-options-textarea"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button
              onClick={save}
              disabled={!form.label.trim()}
              className="bg-cfc-primary hover:bg-cfc-primary-hover"
              data-testid="save-form-field-btn"
            >
              {editingIdx >= 0 ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FormFieldsEditor;
